/** gamee.fan 生产域名，启用「语言 + 类别」路由 */
const GAMEE_FAN_HOSTS = ["gamee.fan", "www.gamee.fan"];

/** 是否启用 /{lang}/{category} 路由（仅 gamee.fan 生产环境） */
export function isLangCategoryRouting() {
  if (process.env.REACT_APP_LANG_CATEGORY_ROUTES !== "true") return false;
  if (typeof window === "undefined") return false;
  return GAMEE_FAN_HOSTS.includes(window.location.hostname.toLowerCase());
}

export const VALID_LANG_CODES = ["en", "ru", "tw"];
export const VALID_ROUTE_CATEGORIES = ["game", "topic", "collection"];

const LANGUAGE_ID_TO_CODE = {
  english: "en",
  russian: "ru",
  chinese_traditional: "tw",
};

const LANGUAGE_CODE_TO_ID = {
  en: "english",
  ru: "russian",
  tw: "chinese_traditional",
};

export function languageIdToCode(langId) {
  return LANGUAGE_ID_TO_CODE[langId] || "en";
}

export function languageCodeToId(code) {
  return LANGUAGE_CODE_TO_ID[code] || "english";
}

function readDefaultLangCode() {
  if (typeof window === "undefined") return "en";
  try {
    const saved = window.localStorage.getItem("language");
    if (saved) {
      return languageIdToCode(
        saved === "chinese_simplified" ? "chinese_traditional" : saved,
      );
    }
  } catch (e) {
    // ignore
  }
  const navLang = (
    window.navigator.language ||
    window.navigator.userLanguage ||
    "en"
  ).toLowerCase();
  if (navLang.startsWith("ru")) return "ru";
  if (navLang.startsWith("zh")) return "tw";
  return "en";
}

export function resolveLangCode(langCode) {
  if (langCode && VALID_LANG_CODES.includes(langCode)) return langCode;
  if (typeof window !== "undefined" && isLangCategoryRouting()) {
    const fromPath = parseLangCategoryPath(window.location.pathname)?.lang;
    if (fromPath) return fromPath;
  }
  return readDefaultLangCode();
}

function normalizePathname(pathname) {
  if (!pathname) return "/";
  if (pathname.length > 1 && pathname.endsWith("/")) {
    return pathname.slice(0, -1);
  }
  return pathname;
}

/**
 * 解析生产环境路由：/{lang}/{category} 或 /{lang}/{type}/{id}
 */
export function parseLangCategoryPath(pathname) {
  const path = normalizePathname(pathname);
  const segments = path.split("/").filter(Boolean);
  if (segments.length === 0) return null;

  const [lang, second, third] = segments;
  if (!VALID_LANG_CODES.includes(lang)) return null;

  if (segments.length === 2 && VALID_ROUTE_CATEGORIES.includes(second)) {
    return { lang, category: second, type: "list" };
  }

  if (segments.length === 3) {
    if (second === "game") {
      return { lang, category: "game", type: "gameDetail", id: third };
    }
    if (second === "collection") {
      return { lang, category: "collection", type: "collectionDetail", id: third };
    }
    if (second === "topic") {
      return { lang, category: "topic", type: "topicDetail", id: third };
    }
    if (second === "post") {
      return { lang, category: "topic", type: "postDetail", id: third };
    }
  }

  return { lang, category: null, type: "unknown" };
}

export function isGameHomePath(pathname) {
  if (isLangCategoryRouting()) {
    const parsed = parseLangCategoryPath(pathname);
    return parsed?.type === "list" && parsed.category === "game";
  }
  const path = normalizePathname(pathname);
  return path === "/gamee" || path === "/gamee/";
}

export function isCollectionsListPath(pathname) {
  if (isLangCategoryRouting()) {
    const parsed = parseLangCategoryPath(pathname);
    return parsed?.type === "list" && parsed.category === "collection";
  }
  return normalizePathname(pathname) === "/gamee/collections";
}

export function isTopicsListPath(pathname) {
  if (isLangCategoryRouting()) {
    const parsed = parseLangCategoryPath(pathname);
    return parsed?.type === "list" && parsed.category === "topic";
  }
  return normalizePathname(pathname) === "/gamee/topics";
}

export function isCollectionsSectionPath(pathname) {
  if (isLangCategoryRouting()) {
    const parsed = parseLangCategoryPath(pathname);
    return (
      parsed &&
      (parsed.category === "collection" || parsed.type === "collectionDetail")
    );
  }
  return (
    pathname.startsWith("/gamee/collections") ||
    pathname.startsWith("/gamee/collection/")
  );
}

export function isTopicsSectionPath(pathname) {
  if (isLangCategoryRouting()) {
    const parsed = parseLangCategoryPath(pathname);
    return (
      parsed &&
      (parsed.category === "topic" ||
        parsed.type === "topicDetail" ||
        parsed.type === "postDetail")
    );
  }
  return (
    pathname.startsWith("/gamee/topics") ||
    pathname.startsWith("/gamee/topic/")
  );
}

export function getCategoryFromPathname(pathname) {
  if (isLangCategoryRouting()) {
    const parsed = parseLangCategoryPath(pathname);
    if (parsed?.category) return parsed.category;
    return "game";
  }
  if (isCollectionsListPath(pathname) || pathname.startsWith("/gamee/collection/")) {
    return "collection";
  }
  if (isTopicsListPath(pathname) || pathname.startsWith("/gamee/topic/") || pathname.startsWith("/gamee/post/")) {
    return "topic";
  }
  return "game";
}

export function gameHomePath({ lang, category } = {}) {
  if (!isLangCategoryRouting()) {
    if (category) {
      return `/gamee/?category=${encodeURIComponent(category)}`;
    }
    return "/gamee/";
  }
  const code = resolveLangCode(lang);
  const base = `/${code}/game`;
  if (category) {
    return `${base}?category=${encodeURIComponent(category)}`;
  }
  return base;
}

export function collectionsListPath({ lang } = {}) {
  if (!isLangCategoryRouting()) return "/gamee/collections";
  return `/${resolveLangCode(lang)}/collection`;
}

export function topicsListPath({ lang } = {}) {
  if (!isLangCategoryRouting()) return "/gamee/topics";
  return `/${resolveLangCode(lang)}/topic`;
}

export function gameDetailPath(gameId, { lang } = {}) {
  if (!isLangCategoryRouting()) return `/gamee/game/${gameId}`;
  return `/${resolveLangCode(lang)}/game/${encodeURIComponent(gameId)}`;
}

export function collectionDetailPath(collectionId, { lang } = {}) {
  if (!isLangCategoryRouting()) {
    return `/gamee/collection/${encodeURIComponent(collectionId)}`;
  }
  return `/${resolveLangCode(lang)}/collection/${encodeURIComponent(collectionId)}`;
}

export function topicDetailPath(topicId, { lang } = {}) {
  if (!isLangCategoryRouting()) {
    return `/gamee/topic/${encodeURIComponent(topicId)}`;
  }
  return `/${resolveLangCode(lang)}/topic/${encodeURIComponent(topicId)}`;
}

export function postDetailPath(postId, { lang, hash } = {}) {
  const id = encodeURIComponent(postId);
  if (!isLangCategoryRouting()) {
    const base = `/gamee/post/${id}`;
    return hash ? `${base}${hash}` : base;
  }
  const base = `/${resolveLangCode(lang)}/post/${id}`;
  return hash ? `${base}${hash}` : base;
}

export function legacyGamePagePath(surname) {
  return `/gamee/games/${surname}`;
}

export function browsePath() {
  return "/gamee";
}

export function switchLangPath(pathname, newLangCode) {
  if (!isLangCategoryRouting()) return null;
  const parsed = parseLangCategoryPath(pathname);
  if (parsed?.type === "gameDetail") {
    return gameDetailPath(parsed.id, { lang: newLangCode });
  }
  if (parsed?.type === "collectionDetail") {
    return collectionDetailPath(parsed.id, { lang: newLangCode });
  }
  if (parsed?.type === "topicDetail") {
    return topicDetailPath(parsed.id, { lang: newLangCode });
  }
  if (parsed?.type === "postDetail") {
    return postDetailPath(parsed.id, { lang: newLangCode });
  }

  const category = getCategoryFromPathname(pathname);
  if (category === "collection") return collectionsListPath({ lang: newLangCode });
  if (category === "topic") return topicsListPath({ lang: newLangCode });
  return gameHomePath({ lang: newLangCode });
}
